import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import withStyles from "isomorphic-style-loader/withStyles";
import { connect } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import {
  serviceCaller,
  ServiceCallerProps,
  withSuperadminLoginRedirect,
} from "../../composition";
import * as commonActions from "../../../utils/actions/common";
import ChatSessionList from "./sessionList";
import { useChatSessionList } from "./sessionList/useChatSessionList";
import ChatSessionThread from "./thread";
import s from "./styles.scss";

const MOBILE_MAX_WIDTH = 767;
const DRAFT_SESSION_ID = "new";

function getIsMobile(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.innerWidth <= MOBILE_MAX_WIDTH;
}

interface Props extends ServiceCallerProps {
  isSuperadmin: boolean;
  hideSelection: () => void;
}

function ChatSessionPage({
  config,
  serviceAccessToken,
  currentCharity,
  params,
  isSuperadmin,
  hideSelection,
}: Props) {
  const navigate = useNavigate();
  const sessionId =
    typeof params.sessionId === "string" ? params.sessionId : undefined;
  const isDraft = sessionId === DRAFT_SESSION_ID;
  const charityId = currentCharity?.id;

  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewportReady, setViewportReady] = useState(false);

  const {
    sessions,
    loading: sessionsLoading,
    error: sessionsError,
    reloadSessions,
    handleSessionSelect,
  } = useChatSessionList({
    config,
    serviceAccessToken,
    charityId,
  });

  useEffect(() => {
    hideSelection();
  }, [hideSelection]);

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    const syncViewport = () => {
      const mobile = media.matches;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
      setViewportReady(true);
    };

    syncViewport();
    media.addEventListener("change", syncViewport);
    return () => media.removeEventListener("change", syncViewport);
  }, []);

  useEffect(() => {
    if (!sidebarOpen) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [sidebarOpen]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(open => !open);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const onSessionSelect = useCallback(
    (id: string) => {
      handleSessionSelect(id);
      if (getIsMobile()) {
        setSidebarOpen(false);
      }
    },
    [handleSessionSelect]
  );

  const onNewSession = useCallback(() => {
    navigate(`/chat-session/${DRAFT_SESSION_ID}`);
    if (getIsMobile()) {
      setSidebarOpen(false);
    }
  }, [navigate]);

  const onSessionCreated = useCallback(async () => {
    await reloadSessions();
  }, [reloadSessions]);

  if (!isSuperadmin) {
    return <Navigate to="/contacts" replace />;
  }

  return (
    <main className={s.main}>
      <div className={s.titleRow}>
        <button
          type="button"
          className={s.menuButton}
          onClick={toggleSidebar}
          aria-label={sidebarOpen ? "Hide conversations" : "Show conversations"}
          aria-expanded={sidebarOpen}
          aria-controls="chatSessionSidebar"
          data-elm-id="chatSessionMenuToggle"
        >
          <i className="fa fa-bars" aria-hidden="true" />
        </button>
        <h1 className={s.pageTitle}>Chat</h1>
      </div>
      <div className={s.container}>
        <button
          type="button"
          className={classNames(s.backdrop, sidebarOpen && s.backdropVisible)}
          aria-label="Close conversations"
          aria-hidden={!(sidebarOpen && isMobile)}
          tabIndex={sidebarOpen && isMobile ? 0 : -1}
          onClick={closeSidebar}
          data-elm-id="chatSessionSidebarBackdrop"
        />
        <aside
          id="chatSessionSidebar"
          className={classNames(
            s.sidebar,
            viewportReady && (sidebarOpen ? s.sidebarOpen : s.sidebarClosed)
          )}
          aria-hidden={viewportReady ? !sidebarOpen : false}
        >
          <div className={s.sidebarHeader}>
            <span>Conversations</span>
            <button
              type="button"
              className={s.newSessionButton}
              onClick={onNewSession}
              aria-label="New conversation"
              data-elm-id="chatSessionNewButton"
            >
              <i className="fa fa-plus" aria-hidden="true" />
            </button>
          </div>
          <div className={s.sessionList}>
            <ChatSessionList
              sessions={sessions}
              loading={sessionsLoading}
              error={sessionsError}
              activeSessionId={isDraft ? undefined : sessionId}
              isDraft={isDraft}
              onSessionSelect={onSessionSelect}
            />
          </div>
        </aside>
        <section className={s.thread}>
          <ChatSessionThread
            key={sessionId ?? "no-session"}
            config={config}
            serviceAccessToken={serviceAccessToken}
            charityId={charityId}
            sessionId={sessionId}
            isDraft={isDraft}
            onSessionCreated={onSessionCreated}
          />
        </section>
      </div>
    </main>
  );
}

const mapStateToProps = state => ({
  isSuperadmin: state.user.isSuperadmin,
});

const mapDispatchToProps = dispatch => ({
  hideSelection: () => {
    dispatch(commonActions.hideSelection());
  },
});

export default serviceCaller(
  withSuperadminLoginRedirect(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(withStyles(s)(ChatSessionPage))
  )
);
